import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { CompareProvider } from "@/context/CompareContext";
import { LanguageProvider } from "@/context/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AIAssistant from "@/components/AIAssistant";
import CompareTray from "@/components/CompareTray";
import MiniCart from "@/components/MiniCart";
import Home from "@/pages/Home";
import Store from "@/pages/Store";
import ProductPage from "@/pages/ProductPage";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import PaymentResult from "@/pages/PaymentResult";
import HeritagePage from "@/pages/HeritagePage";
import BasketBuilder from "@/pages/BasketBuilder";
import Login from "@/pages/Login";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import Cook from "@/pages/Cook";
import BoxPage from "@/pages/BoxPage";
import Compare from "@/pages/Compare";
import QuickView from "@/pages/QuickView";
import Nutrition from "@/pages/Nutrition";
import CountryPage from "@/pages/CountryPage";
import Invoice from "@/pages/Invoice";
import AdminPrintCard from "@/pages/AdminPrintCard";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return null;
}

function AnimatedMain() {
  const location = useLocation();
  return (
    <motion.main
      key={location.pathname}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      <Routes location={location}>
        <Route path="/" element={<Home />} />
        <Route path="/store" element={<Store />} />
        <Route path="/product/:slug" element={<ProductPage />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment/:result" element={<PaymentResult />} />
        <Route path="/heritage" element={<HeritagePage />} />
        <Route path="/heritage/:slug" element={<HeritagePage />} />
        <Route path="/basket-builder" element={<BasketBuilder />} />
        <Route path="/login" element={<Login />} />
        <Route path="/account" element={<Account />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/cook" element={<Cook />} />
        <Route path="/box" element={<BoxPage />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/nutrition" element={<Nutrition />} />
        <Route path="/country/:key" element={<CountryPage />} />
        <Route path="/invoice/:token" element={<Invoice />} />
        <Route path="/admin/print/:orderNo" element={<AdminPrintCard />} />
        <Route path="/p/:sku" element={<QuickView />} />
      </Routes>
    </motion.main>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <CompareProvider>
              <ScrollTop />
              <SmoothScroll />
              <Navbar />
              <AnimatedMain />
              <Footer />
              <AIAssistant />
              <CompareTray />
              <MiniCart />
              <Toaster position="top-center" richColors />
            </CompareProvider>
          </CartProvider>
        </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
